import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ILecture } from '../views/pages/lectures/lecture.interface';
import { LecturesService } from '../views/pages/lectures/lectures.service';
import { LectureValuesData, LanguagesEnum, LectureOnlineTypeEnum,
        LecturePriorityEnum, LectureStatusEnum, LectureTypeEnum, LocaleEnum, AgeGroupEnum, WeekdaysEnum } from './lecture.values';
import { Observable, Subscription, timer } from 'rxjs';
import { debounce, map, startWith } from 'rxjs/operators';
import { ISchool } from '../views/pages/lectures/schools/school.interface';
import { MatAutocompleteActivatedEvent } from '@angular/material/autocomplete';
import { HttpClient } from '@angular/common/http';
import { CitiesService } from '../views/pages/lectures/cities/cities.service';
import { AnimationOptions } from 'ngx-lottie';
import firebase from 'firebase';
import { environment } from '../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';


const ISRAEL = 900;
@Component({
  selector: 'submit-lecture-students',
  templateUrl: './submit-lecture-students.component.html',
  styleUrls: ['./submit-lecture-students.component.scss']
})
export class SubmitLectureStudentsComponent implements OnInit, OnDestroy {

  submitForm: FormGroup;
  ageGroupItems: any;
  cityItems: any;
  countryItems: any;
  languageItems: any;
  weekdaysItems: any;
  typeItems: any;
  ageBindLabel: string;
  cityBindLabel: string;
  countryBindLabel: string;
  langBindLabel: string;
  weekdaysBindLabel: string;
  typeBindLabel: string;
  declarativeFormCaptchaValue: any;
  schoolsItems: ISchool[] = [];
  filteredSchoolsOptions: Observable<ISchool[]>;
  success = false;
  fail = false;
  loading = false;
  invalid = false;
  lang;
  private subscription: Subscription;

  checkOptions: AnimationOptions = {
    path: '/assets/animations/check.json',
    loop: false
  };
  xOptions: AnimationOptions = {
    path: '/assets/animations/x.json',
    loop: false
  };
  loadOptions: AnimationOptions = {
    path: '/assets/animations/loader-white.json'
  };
  defaultAnalytics;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
    public translate: TranslateService,
    private lecturesService: LecturesService,
    private citiesService: CitiesService,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,
    private http: HttpClient) {
      this.activatedRoute.queryParams.subscribe(params => {
        switch (params?.lang) {
          case LocaleEnum.EN:
          case LocaleEnum.HE: {
            this.lang = params.lang;
            this.translate.use(this.lang);
            break;
          }
          default: {
            this.lang = LocaleEnum.HE;
            this.translate.use(this.lang);
            break;
          }
        }
        this.onLangChange(this.lang);
        }
      );
    }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.initFormGroupControls();
    this.subscription = this.translate.onLangChange
      .subscribe((event: LangChangeEvent) => {
        this.onLangChange(event.lang);
    });

    firebase.analytics.isSupported().then((isSupported) => {
      if (isSupported) {
        this.defaultAnalytics = firebase.analytics();
      }
    });

    this.ageGroupItems = LectureValuesData.ageGroups.filter(age => {
      if (age.id !== AgeGroupEnum.ADULTS) {
        return age;
      }
    });
    this.cityItems = LectureValuesData.cities;
    this.countryItems = LectureValuesData.countries;
    this.languageItems = LectureValuesData.languages;
    this.weekdaysItems = LectureValuesData.weekdays.map(item => {
      return {...item, all: this.translate.instant('ALL')};
    });
    this.typeItems = LectureValuesData.types;
    this.schoolsItems = this.lecturesService.getSchoolsAndCitiesCombinedList();

    this.filteredSchoolsOptions = this.submitForm.get('institute_name').valueChanges
      .pipe(
          startWith(''),
          debounce(() => timer(1000)),
          map(value => {
            const filterValue = value.toLowerCase();
            return filterValue.length > 1 ? this.schoolsItems.filter(
                option => option.name.toLowerCase().includes(filterValue)) : [];
          })
      );
  }

  private onLangChange(lang): void {
    this.lang = lang;
    this.ageBindLabel = 'name' + lang.toUpperCase();
    this.cityBindLabel = 'name' + lang.toUpperCase();
    this.countryBindLabel = 'name' + lang.toUpperCase();
    this.langBindLabel = 'name' + lang.toUpperCase();
    this.weekdaysBindLabel = 'name' + lang.toUpperCase();
    this.typeBindLabel = 'name' + lang.toUpperCase();

    switch (this.lang) {
      case LocaleEnum.EN:
        this.document.body.classList.remove('rtl');
        this.document.body.classList.add('ltr');
        break;
      case LocaleEnum.HE:
        this.document.body.classList.remove('ltr');
        this.document.body.classList.add('rtl');
        break;
    }
  }

  private initFormGroupControls(): void {
    this.submitForm = new FormGroup({
      institute_name: new FormControl('', [Validators.required]),
      age_group: new FormControl('', [Validators.required]),
      city: new FormControl('', [Validators.required, Validators.maxLength(100)]),
      country: new FormControl(900, [Validators.required, Validators.maxLength(100)]),
      street: new FormControl('', [Validators.required, Validators.maxLength(100)]),
      house_number: new FormControl('', [, Validators.maxLength(5)]),
      contact_name: new FormControl('', [Validators.required, Validators.maxLength(100)]),
      contact_title: new FormControl('', [Validators.required, Validators.maxLength(100)]),
      contact_email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(100)]),
      contact_phone: new FormControl('', [Validators.required, Validators.pattern('[- +()0-9]+')]),
      class: new FormControl('', [Validators.required]),
      language: new FormControl(LanguagesEnum.HEBREW, [Validators.required]),
      online_type:  new FormControl(LectureOnlineTypeEnum.FRONTAL, [Validators.required]),
      weekdays: new FormControl(''),
      notes: new FormControl('', []),
      // recaptchaReactive: new FormControl(null, Validators.required)
    });
  }

  private isFormValid(formGroup: FormGroup): boolean {
    return formGroup.valid;
  }

  submit(): void {
    if (this.isFormValid(this.submitForm)) {
      try {
        if (this.defaultAnalytics) {
          this.defaultAnalytics.logEvent('sub_lect_students_init', {
            environment: environment.production ? 'prod' : 'dev',
            l_institute: this.submitForm.value.institute_name,
            l_city: this.submitForm.value.city,
            l_date: new Date(),
            l_contact: this.submitForm.value.contact_email
          });
        }
        this.invalid = false;
        this.loading = true;
        this.success = false;
        this.fail = false;

        let weekdays = this.submitForm.value.weekdays;
        if (!weekdays || ( weekdays.length > 0 && weekdays[0] === 'ALL' )) {
          weekdays = Object.values(WeekdaysEnum);
        }
        const newLecture: ILecture = {
          address: {
            country: this.submitForm.value.country,
            city: this.submitForm.value.city,
            street: this.submitForm.value.street,
            house_number: this.submitForm.value.house_number
          },
          institute_name: this.submitForm.value.institute_name,
          age_group: this.submitForm.value.age_group,
          contact_name: this.submitForm.value.contact_name,
          contact_title: this.submitForm.value.contact_title,
          contact_email: this.submitForm.value.contact_email,
          contact_phone: this.submitForm.value.contact_phone,
          class: this.submitForm.value.class,
          language: this.submitForm.value.language,
          weekdays,
          online_type: this.submitForm.value.online_type,
          notes: this.submitForm.value.notes,
          created_at: + new Date(),
          updated_at: + new Date(),
          priority: LecturePriorityEnum.NORMAL,
          status: LectureStatusEnum.PENDING,
          type: LectureTypeEnum.NONPAID,
        };

        this.lecturesService.addLecture(newLecture)
        .then(() => {
          this.translate.get('SUBMIT.SUCCESS_MESSAGE').subscribe((text: string) => {this.toastr.success(text); });
          this.fail = false;
          this.success = true;

          if (this.defaultAnalytics) {
            this.defaultAnalytics.logEvent('sub_lect_students_success', {
              environment: environment.production ? 'prod' : 'dev',
              l_institute: newLecture.institute_name,
              l_city: newLecture.address.city,
              l_date: newLecture.created_at,
              l_contact: newLecture.contact_email
            });
          }
          const formData = this.constructFormData(newLecture);

          this.sendRequestToWP(formData, newLecture).subscribe(
            (response) => {
              if (response && response.status && response.status !== 'mail_sent') {
                console.error(response);
                if (this.defaultAnalytics) {
                  this.defaultAnalytics.logEvent('sub_lect_students_wp_error', {
                    environment: environment.production ? 'prod' : 'dev',
                    l_institute: newLecture.institute_name,
                    l_city: newLecture.address.city,
                    l_date: newLecture.created_at,
                    l_contact: newLecture.contact_email,
                    error: JSON.stringify(response)
                  });
                }
              } else {
                if (this.defaultAnalytics) {
                  this.defaultAnalytics.logEvent('sub_lect_students_wp_success', {
                    environment: environment.production ? 'prod' : 'dev',
                    l_institute: newLecture.institute_name,
                    l_city: newLecture.address.city,
                    l_date: newLecture.created_at,
                    l_contact: newLecture.contact_email
                  });
                }
              }
            },
            (error) => {
              console.error(error);
              if (this.defaultAnalytics) {
                this.defaultAnalytics.logEvent('sub_lect_students_wp_error', {
                  environment: environment.production ? 'prod' : 'dev',
                  l_institute: newLecture.institute_name,
                  l_city: newLecture.address.city,
                  l_date: newLecture.created_at,
                  l_contact: newLecture.contact_email,
                  error: JSON.stringify(error)
                });
              }
            }
          );
        })
        .catch((err) => {
          console.error(err);
          this.success = false;
          this.fail = true;
          this.translate.get('SUBMIT.FAILED_MESSAGE').subscribe((text: string) => {this.toastr.error(text); });
          if (this.defaultAnalytics) {
            this.defaultAnalytics.logEvent('sub_lect_students_error', {
              environment: environment.production ? 'prod' : 'dev',
              l_institute: newLecture.institute_name,
              l_city: newLecture.address.city,
              l_date: newLecture.created_at,
              l_contact: newLecture.contact_email,
              error: JSON.stringify(err)
            });
          }
        })
        .finally(() => {
          this.initFormGroupControls();
          this.loading = false;
        });
      } catch (e) {
        console.error(e);
        if (this.defaultAnalytics) {
          this.defaultAnalytics.logEvent('sub_lect_students_error', {
            environment: environment.production ? 'prod' : 'dev',
            error: JSON.stringify(e)
          });
        }
      }

    } else {
      this.invalid = true;
      console.error('Form is invalid', this.submitForm);
      this.submitForm.markAllAsTouched();
    }
  }

  onInstituteAutoCompleteSelected(selectedOption: MatAutocompleteActivatedEvent): void {
    this.submitForm.get('city').setValue(selectedOption.option.id);
  }

  sendRequestToWP(formData: FormData, newLecture: ILecture): Observable<any> {
    if (this.defaultAnalytics) {
      this.defaultAnalytics.logEvent('sub_lect_students_wp', {
        environment: environment.production ? 'prod' : 'dev',
        l_institute: newLecture.institute_name,
        l_city: newLecture.address?.city,
        l_date: newLecture.created_at,
        l_contact: newLecture.contact_email
      });
    }
    return this.http.post<any>('https://wp.spaceil.com/he/wp-json/contact-form-7/v1/contact-forms/267/feedback',
      formData);
  }

  constructFormData(newLecture: ILecture): FormData {
    const cityItem = this.citiesService.getCityById(newLecture.address.city);
    const district = Number(cityItem.district_id);
    const districtDisplay = this.lecturesService.getDistrictDisplayName(district, 'skyz');
    const cityDisplay = this.lecturesService.getCityDisplayName(cityItem, LocaleEnum.HE);
    const ageGroupDisplay = this.lecturesService.getAgeGroupDisplayName(newLecture, 'skyz');
    const langDisplay = this.lecturesService.getLangDisplayName(newLecture, LocaleEnum.HE);
    let address = newLecture.address.street;
    if (newLecture.address.house_number) {
      address += ' ' + newLecture.address.house_number;
    }
    const formData: any = new FormData();
    formData.append('organization_name', newLecture.institute_name);
    formData.append('organization_type', ageGroupDisplay);
    formData.append('organization_area', districtDisplay);
    formData.append('address_city', cityDisplay);
    formData.append('address_street', address);
    formData.append('contact_name', newLecture.contact_name);
    formData.append('contact_title', newLecture.contact_title);
    formData.append('contact_email', newLecture.contact_email);
    formData.append('contact_phone', newLecture.contact_phone);
    formData.append('class_age', newLecture.class ? newLecture.class : ' ');
    formData.append('langs', langDisplay);
    formData.append('notes', newLecture.notes ? newLecture.notes : ' ' );
    formData.append('xoveret', '');
    formData.append('skyzid', '1400000116243');

    return formData;
  }

  onFormChange(): void {
    this.success = false;
    this.fail = false;
  }

  isIsrael(): boolean {
    return this.submitForm.get('country').value === ISRAEL;
  }

  onCountryChange(): void {
    this.submitForm.get('city').setValue('');
  }

  get weekdaysControl(): FormControl { return (this.submitForm.get('weekdays') as FormControl); }
}
